import type { AcademicYearSummary, CampusSummary, SchoolSummary } from '@school-erp/types';
import {
  createAcademicYearSchema,
  createCampusSchema,
  updateSchoolSchema,
} from '@school-erp/validation';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router';
import { api, ApiError } from '../../api/http';
import { useAuth } from '../../auth/AuthContext';
import { TextField } from '../../components/TextField';
import { TimezoneCurrencyLists } from '../../components/Lists';
import { t } from '../../i18n';

const errorText = (error: unknown) =>
  error instanceof ApiError ? error.message : t('common.actionError');

export function SchoolDetailPage() {
  const { schoolId = '' } = useParams();
  const school = useQuery({
    queryKey: ['schools', schoolId],
    queryFn: () => api<SchoolSummary>(`/schools/${schoolId}`),
  });

  if (school.isPending) return <p role="status">{t('common.loading')}</p>;
  if (school.isError)
    return (
      <p role="alert" className="text-red-700">
        {t('common.loadError')}
      </p>
    );

  return (
    <div className="mx-auto max-w-4xl space-y-8">
      <div>
        <Link to="/schools" className="text-sm text-brand-700 underline">
          {t('schools.back')}
        </Link>
        <h1 className="mt-1 text-2xl font-bold">{school.data.name}</h1>
        <p className="font-mono text-xs text-slate-600">{school.data.code}</p>
      </div>
      <SchoolDetails school={school.data} />
      <CampusesSection schoolId={schoolId} />
      <AcademicYearsSection schoolId={schoolId} />
    </div>
  );
}

function Message({ message }: { message: { kind: 'ok' | 'error'; text: string } | null }) {
  if (!message) return null;
  return (
    <p
      role={message.kind === 'error' ? 'alert' : 'status'}
      className={message.kind === 'error' ? 'text-sm text-red-700' : 'text-sm text-green-800'}
    >
      {message.text}
    </p>
  );
}

function SchoolDetails({ school }: { school: SchoolSummary }) {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const editable = can('schools.update', school.id);
  const initial = {
    name: school.name,
    city: school.city ?? '',
    phone: school.phone ?? '',
    email: school.email ?? '',
    timezone: school.timezone,
    currencyCode: school.currencyCode,
    language: school.language,
  };
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  useEffect(() => setForm(initial), [school]);

  const save = useMutation({
    mutationFn: (body: object) => api(`/schools/${school.id}`, { method: 'PATCH', body }),
    onSuccess: () => {
      setMessage({ kind: 'ok', text: t('common.saved') });
      void queryClient.invalidateQueries({ queryKey: ['schools'] });
    },
    onError: (e) => setMessage({ kind: 'error', text: errorText(e) }),
  });

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    setMessage(null);
    const body = {
      ...form,
      city: form.city || null,
      phone: form.phone || null,
      email: form.email || null,
    };
    const parsed = updateSchoolSchema.safeParse(body);
    if (!parsed.success) {
      setErrors(Object.fromEntries(parsed.error.issues.map((i) => [String(i.path[0]), i.message])));
      return;
    }
    setErrors({});
    save.mutate(parsed.data);
  }

  const bind = (name: keyof typeof form) => ({
    value: form[name],
    error: errors[name],
    disabled: !editable,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
      setForm({ ...form, [name]: e.target.value }),
  });

  return (
    <section aria-labelledby="details-title" className="space-y-3">
      <h2 id="details-title" className="text-lg font-semibold">
        {t('schools.details')}
      </h2>
      <form
        onSubmit={onSubmit}
        noValidate
        className="grid gap-4 rounded-lg border border-slate-200 bg-white p-4 sm:grid-cols-2"
      >
        <TextField label={t('schools.name')} {...bind('name')} />
        <TextField label={t('schools.city')} {...bind('city')} />
        <TextField label={t('schools.phone')} {...bind('phone')} />
        <TextField label={t('schools.email')} {...bind('email')} />
        <TextField label={t('org.timezone')} list="timezone-options" {...bind('timezone')} />
        <TextField label={t('org.currency')} list="currency-options" {...bind('currencyCode')} />
        <TextField label={t('schools.language')} {...bind('language')} />
        <div className="sm:col-span-2 space-y-2">
          <Message message={message} />
          {editable && (
            <button
              type="submit"
              disabled={save.isPending}
              className="rounded bg-brand-700 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-900 disabled:opacity-50"
            >
              {save.isPending ? t('common.saving') : t('common.save')}
            </button>
          )}
        </div>
        <TimezoneCurrencyLists />
      </form>
    </section>
  );
}

function CampusesSection({ schoolId }: { schoolId: string }) {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: '', code: '' });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const key = ['schools', schoolId, 'campuses'];

  const campuses = useQuery({
    queryKey: key,
    queryFn: () => api<CampusSummary[]>(`/schools/${schoolId}/campuses`),
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: key });
  const fail = (e: unknown) => setMessage({ kind: 'error', text: errorText(e) });

  const create = useMutation({
    mutationFn: (body: object) => api(`/schools/${schoolId}/campuses`, { method: 'POST', body }),
    onSuccess: () => {
      setAdding(false);
      setForm({ name: '', code: '' });
      setMessage(null);
      void refresh();
    },
    onError: (e) =>
      e instanceof ApiError && e.code === 'CAMPUS_CODE_TAKEN'
        ? setErrors({ code: e.message })
        : fail(e),
  });
  const makeMain = useMutation({
    mutationFn: (id: string) =>
      api(`/schools/${schoolId}/campuses/${id}`, { method: 'PATCH', body: { isMain: true } }),
    onSuccess: refresh,
    onError: fail,
  });
  const remove = useMutation({
    mutationFn: (id: string) => api(`/schools/${schoolId}/campuses/${id}`, { method: 'DELETE' }),
    onSuccess: refresh,
    onError: fail,
  });

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    const parsed = createCampusSchema.safeParse(form);
    if (!parsed.success) {
      setErrors(Object.fromEntries(parsed.error.issues.map((i) => [String(i.path[0]), i.message])));
      return;
    }
    setErrors({});
    create.mutate(parsed.data);
  }

  return (
    <section aria-labelledby="campuses-title" className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 id="campuses-title" className="text-lg font-semibold">
          {t('campuses.title')}
        </h2>
        {can('campuses.create', schoolId) && (
          <button
            type="button"
            aria-expanded={adding}
            onClick={() => setAdding((o) => !o)}
            className="rounded border border-slate-300 px-3 py-1.5 text-sm font-medium hover:bg-slate-50"
          >
            {adding ? t('common.cancel') : t('campuses.add')}
          </button>
        )}
      </div>
      <Message message={message} />
      {adding && (
        <form
          onSubmit={onSubmit}
          noValidate
          className="grid gap-4 rounded-lg border border-slate-200 bg-white p-4 sm:grid-cols-3"
        >
          <TextField
            label={t('schools.name')}
            value={form.name}
            error={errors.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <TextField
            label={t('schools.code')}
            value={form.code}
            error={errors.code}
            onChange={(e) => setForm({ ...form, code: e.target.value })}
          />
          <div className="self-end">
            <button
              type="submit"
              disabled={create.isPending}
              className="rounded bg-brand-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
            >
              {t('common.add')}
            </button>
          </div>
        </form>
      )}
      {campuses.isPending && <p role="status">{t('common.loading')}</p>}
      {campuses.isError && (
        <p role="alert" className="text-red-700">
          {t('common.loadError')}
        </p>
      )}
      {campuses.data && (
        <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200 bg-white text-sm">
          {campuses.data.length === 0 && (
            <li className="p-4 text-slate-600">{t('campuses.empty')}</li>
          )}
          {campuses.data.map((campus) => (
            <li key={campus.id} className="flex flex-wrap items-center justify-between gap-2 p-3">
              <span>
                <span className="font-medium">{campus.name}</span>{' '}
                <span className="font-mono text-xs text-slate-600">{campus.code}</span>{' '}
                {campus.isMain && (
                  <span className="rounded bg-brand-100 px-2 py-0.5 text-xs font-medium text-brand-900">
                    {t('campuses.main')}
                  </span>
                )}
              </span>
              <span className="flex gap-2">
                {!campus.isMain && can('campuses.update', schoolId) && (
                  <button
                    type="button"
                    onClick={() => makeMain.mutate(campus.id)}
                    className="rounded border border-slate-300 px-2 py-1 text-xs font-medium hover:bg-slate-50"
                  >
                    {t('campuses.makeMain')}
                  </button>
                )}
                {can('campuses.delete', schoolId) && (
                  <button
                    type="button"
                    onClick={() =>
                      window.confirm(t('common.confirmDelete', { name: campus.name })) &&
                      remove.mutate(campus.id)
                    }
                    className="rounded border border-red-300 px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-50"
                  >
                    {t('common.delete')}
                  </button>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function AcademicYearsSection({ schoolId }: { schoolId: string }) {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: '', startDate: '', endDate: '', isCurrent: false });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const key = ['schools', schoolId, 'years'];

  const years = useQuery({
    queryKey: key,
    queryFn: () => api<AcademicYearSummary[]>(`/schools/${schoolId}/academic-years`),
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: key });
  const fail = (e: unknown) => setMessage({ kind: 'error', text: errorText(e) });

  const create = useMutation({
    mutationFn: (body: object) =>
      api(`/schools/${schoolId}/academic-years`, { method: 'POST', body }),
    onSuccess: () => {
      setAdding(false);
      setForm({ name: '', startDate: '', endDate: '', isCurrent: false });
      setMessage(null);
      void refresh();
    },
    onError: fail,
  });
  const makeCurrent = useMutation({
    mutationFn: (id: string) =>
      api(`/schools/${schoolId}/academic-years/${id}/make-current`, { method: 'POST' }),
    onSuccess: refresh,
    onError: fail,
  });
  const remove = useMutation({
    mutationFn: (id: string) =>
      api(`/schools/${schoolId}/academic-years/${id}`, { method: 'DELETE' }),
    onSuccess: refresh,
    onError: fail,
  });

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    const parsed = createAcademicYearSchema.safeParse(form);
    if (!parsed.success) {
      setErrors(Object.fromEntries(parsed.error.issues.map((i) => [String(i.path[0]), i.message])));
      return;
    }
    setErrors({});
    create.mutate(parsed.data);
  }

  return (
    <section aria-labelledby="years-title" className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 id="years-title" className="text-lg font-semibold">
          {t('years.title')}
        </h2>
        {can('academic_years.create', schoolId) && (
          <button
            type="button"
            aria-expanded={adding}
            onClick={() => setAdding((o) => !o)}
            className="rounded border border-slate-300 px-3 py-1.5 text-sm font-medium hover:bg-slate-50"
          >
            {adding ? t('common.cancel') : t('years.add')}
          </button>
        )}
      </div>
      <Message message={message} />
      {adding && (
        <form
          onSubmit={onSubmit}
          noValidate
          className="grid gap-4 rounded-lg border border-slate-200 bg-white p-4 sm:grid-cols-3"
        >
          <TextField
            label={t('years.name')}
            placeholder="2026-27"
            value={form.name}
            error={errors.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <TextField
            label={t('years.start')}
            type="date"
            value={form.startDate}
            error={errors.startDate}
            onChange={(e) => setForm({ ...form, startDate: e.target.value })}
          />
          <TextField
            label={t('years.end')}
            type="date"
            value={form.endDate}
            error={errors.endDate}
            onChange={(e) => setForm({ ...form, endDate: e.target.value })}
          />
          <label className="flex items-center gap-2 text-sm sm:col-span-2">
            <input
              type="checkbox"
              checked={form.isCurrent}
              onChange={(e) => setForm({ ...form, isCurrent: e.target.checked })}
            />
            {t('years.currentOnCreate')}
          </label>
          <div>
            <button
              type="submit"
              disabled={create.isPending}
              className="rounded bg-brand-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
            >
              {t('common.add')}
            </button>
          </div>
        </form>
      )}
      {years.isPending && <p role="status">{t('common.loading')}</p>}
      {years.isError && (
        <p role="alert" className="text-red-700">
          {t('common.loadError')}
        </p>
      )}
      {years.data && (
        <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200 bg-white text-sm">
          {years.data.length === 0 && <li className="p-4 text-slate-600">{t('years.empty')}</li>}
          {years.data.map((year) => (
            <li key={year.id} className="flex flex-wrap items-center justify-between gap-2 p-3">
              <span>
                <span className="font-medium">{year.name}</span>{' '}
                <span className="text-slate-600">
                  {year.startDate} → {year.endDate}
                </span>{' '}
                {year.isCurrent && (
                  <span className="rounded bg-green-100 px-2 py-0.5 text-xs font-medium text-green-900">
                    {t('years.current')}
                  </span>
                )}
              </span>
              <span className="flex gap-2">
                {!year.isCurrent && can('academic_years.update', schoolId) && (
                  <button
                    type="button"
                    onClick={() => makeCurrent.mutate(year.id)}
                    className="rounded border border-slate-300 px-2 py-1 text-xs font-medium hover:bg-slate-50"
                  >
                    {t('years.makeCurrent')}
                  </button>
                )}
                {!year.isCurrent && can('academic_years.delete', schoolId) && (
                  <button
                    type="button"
                    onClick={() =>
                      window.confirm(t('common.confirmDelete', { name: year.name })) &&
                      remove.mutate(year.id)
                    }
                    className="rounded border border-red-300 px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-50"
                  >
                    {t('common.delete')}
                  </button>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
